// components/Greeting.jsx
import React, { Fragment, useEffect, useState, useRef } from "react";
import gsap from "gsap";
import { Draggable } from "gsap/Draggable";
import { InertiaPlugin } from "gsap/InertiaPlugin";
import bgImage from "../../assets/images/blue.jpg";
import img1 from "../../assets/images/HeroSection/Artboard1.png";
import img2 from "../../assets/images/HeroSection/Artboard2.png";
import img3 from "../../assets/images/HeroSection/Artboard3.png";
import img4 from "../../assets/images/HeroSection/Artboard4.png";
import img5 from "../../assets/images/HeroSection/Artboard5.png";
import img6 from "../../assets/images/HeroSection/Artboard6.png";
import spinVideo from "../../assets/videos/spin.webm";
import { Link } from "react-router-dom";
gsap.registerPlugin(Draggable, InertiaPlugin);

const Greeting = () => {
  const [word, setWord] = useState("photo");
  const heroRef = useRef(null);
  const cardsRefs = useRef([]);
  const [cardsData, setCardsData] = useState([]);
  const [imagesLoaded, setImagesLoaded] = useState({});

  // ✅ word toggle — completely independent, runs once on mount only
  useEffect(() => {
    const intervalId = setInterval(() => {
      setWord((prev) => (prev === "photo" ? "360°" : "photo"));
    }, 2000);
    return () => clearInterval(intervalId);
  }, []);

  // ✅ Preload above-the-fold images eagerly using link preload
  useEffect(() => {
    const aboveFoldImages = [img2, img1]; // first two visible cards
    aboveFoldImages.forEach((src) => {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = src;
      document.head.appendChild(link);
    });
  }, []);

  // Set fixed card positions
  useEffect(() => {
    const updateCards = () => {
      const isMobile = window.innerWidth < 768;
      if (!isMobile) {
        setCardsData([
          { id: 1, src: img2, left: "5%", top: "15%", priority: true },
          { id: 2, src: img1, left: "10%", top: "40%", priority: true },
          { id: 3, src: img5, left: "5%", top: "65%", priority: false },
          { id: 4, src: img3, left: "80%", top: "15%", priority: false },
          { id: 5, src: img4, left: "70%", top: "40%", priority: false },
          { id: 6, src: img6, left: "80%", top: "65%", priority: false },
        ]);
      }
    };

    updateCards();
    window.addEventListener("resize", updateCards);
    return () => window.removeEventListener("resize", updateCards);
  }, []);

  // Draggable + entrance animation — only when cardsData changes
  useEffect(() => {
    const cards = cardsRefs.current.filter(Boolean);
    if (!cards.length) return;

    // Kill previous Draggables
    cards.forEach((card) => {
      const draggable = Draggable.get(card);
      if (draggable) draggable.kill();
      card.style.willChange = "transform";
    });

    Draggable.create(cards, {
      type: "x,y",
      bounds: window,
      edgeResistance: 0.65,
      inertia: true,
      onDragStart() {
        this.target.style.zIndex = "30";
      },
      onDragEnd() {
        this.target.style.zIndex = "";
      },
    });

    const timeline = gsap.timeline();
    timeline.from(heroRef.current.querySelectorAll("p, h1"), {
      y: 50,
      opacity: 0,
      stagger: 0.2,
      duration: 1,
      ease: "power3.out",
    });

    timeline.from(
      cards,
      {
        y: 30,
        opacity: 0,
        stagger: 0.15,
        duration: 1,
        ease: "power3.out",
      },
      "-=0.5"
    );

    return () => {
      cards.forEach((card) => {
        const draggable = Draggable.get(card);
        if (draggable) draggable.kill();
      });
    };
  }, [cardsData]);

  // ✅ Track image load state for fade-in effect
  const handleImageLoad = (id) => {
    setImagesLoaded((prev) => ({ ...prev, [id]: true }));
  };

  return (
    <Fragment>
      <section
        ref={heroRef}
        className="w-full h-[100vh] pt-[100px] flex flex-col justify-start items-center relative overflow-hidden bg-gradient-to-b from-black/0 to-black"
      >
        <img
          src={bgImage}
          alt=""
          fetchpriority="high"
          decoding="async"
          aria-hidden="true"
          className="absolute top-0 left-0 w-full h-full object-cover opacity-30 -z-10 pointer-events-none"
          style={{ willChange: "auto" }}
        />

        {/* Hero Text */}
        <div className="text-center z-10">
          <p className="text-[#76cfff] font-poppins uppercase tracking-[4px] text-[18px] font-bold leading-[24px] mb-10 sm:text-[16px] sm:mb-6 xs:text-[14px] xs:mb-5 max-[400px]:text-[12px] max-[400px]:mb-5">
            Looking For Saving Memories?
          </p>
          <h1 className="text-white font-poppins uppercase font-semibold mt-0 mb-[30px] text-[160px] leading-[170px] sm:text-[90px] sm:leading-[100px] max-[992px]:text-[110px] max-[992px]:leading-[120px] max-[1200px]:text-[130px] max-[1200px]:leading-[140px] max-[576px]:text-[70px] max-[576px]:leading-[80px] max-[400px]:text-[50px] max-[400px]:leading-[60px]">
            Welcome{" "}
            <span
              style={{
                fontFamily: "Playfair, serif",
                fontStyle: "italic",
                fontSize: "90px",
              }}
            >
              to
            </span>
            <br />
            <span className="text-[#86cfff] text-center" >Luster</span>
            <br /> <span style={{fontSize:"70%"}}>{word} Booth</span>
          </h1>
        </div>

        {/* ✅ Draggable Cards — priority eager for first 2, lazy for rest */}
        <div className="absolute inset-0 pointer-events-none">
          {cardsData.map((card, i) => (
            <div
              key={card.id}
              ref={(el) => (cardsRefs.current[i] = el)}
              className="draggable-card absolute pointer-events-auto"
              style={{
                left: card.left,
                top: card.top,
                width: window.innerWidth < 768 ? "150px" : "300px",
                zIndex: 20 - i,
              }}
            >
              <img
                src={card.src}
                alt={`Draggable ${card.id}`}
                loading={card.priority ? "eager" : "lazy"}
                fetchpriority={card.priority ? "high" : "low"}
                decoding={card.priority ? "sync" : "async"}
                onLoad={() => handleImageLoad(card.id)}
                className="w-full h-auto rounded-xl object-cover shadow-2xl"
                style={{
                  opacity: imagesLoaded[card.id] ? 1 : 0,
                  transition: "opacity 0.4s ease",
                  willChange: "opacity",
                }}
              />
            </div>
          ))}
        </div>

        <Link to="/SpinWin" className="relative block group">
          {/* ✅ Video — fixed for iOS: playsInline prevents native player */}
          <video
            src={spinVideo}
            autoPlay
            loop
            muted
            playsInline
            disablePictureInPicture
            disableRemotePlayback
            preload="none"
            className="opacity-30 -z-20 w-full pointer-events-none"
          />

          {/* Overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Background watermark text */}
            {/* <span
              className="absolute inset-0 flex justify-center items-center text-white/10 font-bold select-none pointer-events-none z-0 text-[clamp(2rem,8vw,90px)]"
              style={{ fontFamily: "Playfair, serif", fontStyle: "italic" }}
            >
              Spin & Win
            </span> */}

            {/* Foreground heading */}
            <h2
              className="text-white font-bold tracking-wide group-hover:scale-110 transition-transform duration-300 text-[clamp(2rem,8vw,90px)]"
              style={{ fontFamily: "Playfair, serif", fontStyle: "italic" }}
            >
              Spin{" "}
              <span
                style={{
                  fontFamily: "sans-serif",
                  fontStyle: "normal",
                  fontWeight: "400",
                  fontSize: "clamp(1.5rem,6vw,70px)",
                }}
              >
                &amp;
              </span>{" "}
              Win
            </h2>
          </div>
        </Link>
      </section>
    </Fragment>
  );
};

export default Greeting;